import React, { useState, useEffect } from 'react'
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  Grid,
  TextField,
  MenuItem,
  Divider,
  Checkbox,
  ListItemText,
} from '@mui/material'
import { Close, Delete } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import {
  PlayerTransactionFilters,
  PlayerTransactionFilterOptions,
} from '@/state/player-transactions/models'

interface TransactionsFiltersDrawerProps {
  open: boolean
  onClose: () => void
  onApply: (filters: Partial<PlayerTransactionFilters>) => void
  currentFilters: PlayerTransactionFilters
  filterOptions: PlayerTransactionFilterOptions | null
}

export const TransactionsFiltersDrawer = ({
  open,
  onClose,
  onApply,
  currentFilters,
  filterOptions,
}: TransactionsFiltersDrawerProps) => {
  const { t } = useTranslation()
  const [localFilters, setLocalFilters] =
    useState<PlayerTransactionFilters>(currentFilters)

  useEffect(() => {
    if (open) {
      setLocalFilters(currentFilters)
    }
  }, [open, currentFilters])

  const handleChange = (field: keyof PlayerTransactionFilters, value: any) => {
    setLocalFilters((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleApply = () => {
    onApply(localFilters)
    onClose()
  }

  const handleClear = () => {
    setLocalFilters({})
  }

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box
        width={400}
        p={2}
        display="flex"
        flexDirection="column"
        height="100%"
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Box display="flex" alignItems="center">
            <IconButton onClick={onClose} edge="start" sx={{ mr: 1 }}>
              <Close />
            </IconButton>
            <Typography variant="h6" fontWeight="bold">
              {t('transaction.filters')}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Box flex={1} sx={{ overflowY: 'auto' }}>
          <Grid container spacing={2}>
            {/* Transaction Type */}
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label={t('transaction.transactionTypeLabel')}
                value={localFilters.transactionType || []}
                onChange={(e) =>
                  handleChange('transactionType', e.target.value)
                }
                SelectProps={{
                  multiple: true,
                  renderValue: (selected) => {
                    const selectedArray = selected as string[]
                    return selectedArray
                      .map((type) =>
                        t(`transaction.transactionTypeOptions.${type}`),
                      )
                      .join(', ')
                  },
                }}
              >
                {filterOptions?.transactionType?.map((type) => (
                  <MenuItem key={type} value={type}>
                    <Checkbox
                      checked={
                        (localFilters.transactionType || []).indexOf(type) > -1
                      }
                    />
                    <ListItemText
                      primary={t(`transaction.transactionTypeOptions.${type}`)}
                    />
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label={t('transaction.transactionStatus')}
                value={localFilters.transactionStatus || []}
                onChange={(e) =>
                  handleChange('transactionStatus', e.target.value)
                }
                SelectProps={{
                  multiple: true,
                }}
              >
                {filterOptions?.transactionStatus?.map((status) => (
                  <MenuItem key={status} value={status}>
                    {t(`transaction.transactionStatusOptions.${status}`)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid> */}

            {/* Transaction Subtype */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('transaction.transactionSubType')}
                value={localFilters.transactionSubType || ''}
                onChange={(e) =>
                  handleChange('transactionSubType', e.target.value)
                }
                // Placeholder as simple text/select for now
              />
            </Grid>

            {/* Submitted Date */}
            <Grid item xs={12}>
              <Typography variant="caption" color="textSecondary">
                {t('transaction.submittedDate')}
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <TextField
                    type="date"
                    fullWidth
                    value={localFilters.submittedDate || ''}
                    onChange={(e) =>
                      handleChange('submittedDate', e.target.value)
                    }
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Completed Date */}
            <Grid item xs={12}>
              <Typography variant="caption" color="textSecondary">
                {t('transaction.completedDate')}
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <TextField
                    type="date"
                    fullWidth
                    value={localFilters.completedDate || ''}
                    onChange={(e) =>
                      handleChange('completedDate', e.target.value)
                    }
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Wallet Type */}
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label={t('transaction.walletTypeText')}
                value={localFilters.walletType || ''}
                onChange={(e) => handleChange('walletType', e.target.value)}
              >
                {filterOptions?.walletType?.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Provider */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('transaction.provider')}
                value={localFilters.provider || ''}
                onChange={(e) => handleChange('provider', e.target.value)}
              />
            </Grid>

            {/* Group */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('transaction.group')}
                value={localFilters.group || ''}
                onChange={(e) => handleChange('group', e.target.value)}
              />
            </Grid>

            {/* Subgroup */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('transaction.subGroup')}
                value={localFilters.subGroup || ''}
                onChange={(e) => handleChange('subGroup', e.target.value)}
              />
            </Grid>
          </Grid>
        </Box>

        <Box display="flex" justifyContent="space-between" mt={2} pt={2}>
          <Button
            startIcon={<Delete />}
            color="error"
            onClick={handleClear}
            sx={{
              textTransform: 'none',
              color: 'error.main',
              '&:hover': {
                backgroundColor: 'error.lighter',
              },
            }}
          >
            {t('labels.clear')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleApply}
            sx={{ px: 2 }}
          >
            {t('labels.applyFilter')}
          </Button>
        </Box>
      </Box>
    </Drawer>
  )
}
